import { Link, Outlet, useNavigate } from "react-router-dom";

import { clearToken, currentClaims } from "../lib/auth";
import { adminRootPath, canSeeAdmin } from "../lib/adminNav";
import { useEnterpriseBranding } from "../lib/useEnterpriseBranding";

/** Persistent header + main-content shell for every authenticated
 * page. Kept intentionally minimal — the goal is fast comprehension
 * for godown heads, not a chrome-heavy dashboard. */
export function Layout() {
  const claims = currentClaims();
  const navigate = useNavigate();
  const { enterprise } = useEnterpriseBranding();

  const logout = () => {
    clearToken();
    navigate("/login", { replace: true });
  };

  const adminPath = adminRootPath(claims);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link
            to="/"
            className="flex items-center gap-3"
            aria-label="AgriLens.AI"
          >
            {/* Full AgriLens logo (emblem + wordmark + tagline). Height
                is capped so the header doesn't dominate the viewport;
                width scales via aspect ratio. */}
            <img
              src="/logo-app.png"
              alt="AgriLens.AI"
              className="h-12 w-auto"
            />
            {/* Co-branding: the signed-in user's enterprise. State &
                district heads (and admins) see their employer's logo
                and name here; agrilens_super has no home enterprise so
                nothing extra renders. */}
            {enterprise && (
              <span className="flex items-center gap-2 pl-3 border-l border-gray-200">
                {enterprise.logo_url && (
                  <img
                    src={enterprise.logo_url}
                    alt={enterprise.name}
                    className="h-9 w-9 object-contain rounded"
                  />
                )}
                <span className="hidden sm:inline text-sm font-semibold text-gray-700">
                  {enterprise.name}
                </span>
              </span>
            )}
          </Link>
          {claims && (
            <div className="flex items-center gap-4 text-sm">
              <Link to="/" className="hidden sm:inline text-gray-700 hover:text-agrilens-green-dark">
                Dashboard
              </Link>
              {canSeeAdmin(claims) && adminPath && (
                <Link
                  to={adminPath}
                  className="text-gray-700 hover:text-agrilens-green-dark"
                >
                  Admin
                </Link>
              )}
              {claims.email && (
                <span className="hidden sm:inline text-gray-500">
                  {claims.email}
                </span>
              )}
              <button
                onClick={logout}
                className="bg-agrilens-green hover:bg-agrilens-green-dark text-white px-3 py-1 rounded"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full p-4">
        <Outlet />
      </main>

      <footer className="text-xs text-gray-500 text-center py-3">
        AgriLens AI
      </footer>
    </div>
  );
}
